import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Flex, Text, Switch, Button, useToast, VStack, HStack,
    Badge, Divider, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
    IconButton, SimpleGrid, Tooltip, Alert, AlertIcon, Input, Select,
    NumberInput, NumberInputField, Checkbox, CheckboxGroup, Stack,
    FormControl, FormLabel, FormHelperText, Textarea, Spinner,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
    ModalCloseButton, useDisclosure,
} from '@chakra-ui/react';
import {
    FaSyncAlt, FaPlay, FaCheck, FaTimes, FaTrash, FaPlus, FaEye, FaEdit, FaDownload,
} from 'react-icons/fa';
import {
    getRetiraAtraso, updateRetiraAtrasoConfig, rodarRetiraAtrasoAgora,
    criarRetiraAtrasoArgumento, atualizarRetiraAtrasoArgumento, removerRetiraAtrasoArgumento,
    getRetiraAtrasoNegociacao, aprovarRetiraAtrasoNegociacao, negarRetiraAtrasoNegociacao,
    exportarRetiraAtrasoConversas,
} from '../services/api';

const CONTAS_ML = ['1', '2'];

const CANAIS = [
    { valor: 'mlchat', label: 'Chat do ML (negocia direto com o assistente virtual)' },
    { valor: 'auto', label: 'Pede atendente humano e, se o ML transferir, vai pro WhatsApp' },
    { valor: 'whatsapp', label: 'Só WhatsApp (falha se o ML não transferir)' },
];

// Grade de horário indexada por dia da semana (0=domingo ... 6=sábado),
// igual à do bot de atendimento e à do bot de ligação.
const DIAS_SEMANA = [
    { v: 1, label: 'Seg' }, { v: 2, label: 'Ter' }, { v: 3, label: 'Qua' },
    { v: 4, label: 'Qui' }, { v: 5, label: 'Sex' }, { v: 6, label: 'Sáb' },
    { v: 0, label: 'Dom' },
];

const gradeDefault = () =>
    [0, 1, 2, 3, 4, 5, 6].map((d) => ({ ativo: d >= 1 && d <= 5, inicio: '08:00', fim: '18:00' }));

const normalizarGrade = (g) => {
    const base = gradeDefault();
    if (Array.isArray(g)) {
        g.forEach((d, i) => {
            if (i < 7 && d && typeof d === 'object') {
                base[i] = { ativo: !!d.ativo, inicio: d.inicio || '08:00', fim: d.fim || '18:00' };
            }
        });
    }
    return base;
};

const STATUS_BADGE = {
    aguardando_aprovacao: { colorScheme: 'purple', label: 'aguardando aprovação' },
    aprovada: { colorScheme: 'blue', label: 'aprovada' },
    em_andamento: { colorScheme: 'blue', label: 'negociando' },
    aguardando_retorno: { colorScheme: 'yellow', label: 'esperando o suporte' },
    concluida: { colorScheme: 'green', label: 'concluída' },
    negada: { colorScheme: 'orange', label: 'negada na tela' },
    expirada: { colorScheme: 'gray', label: 'expirada' },
    falha: { colorScheme: 'red', label: 'falha' },
};

const RESULTADO_BADGE = {
    excluido: { colorScheme: 'green', label: 'atraso excluído' },
    negado: { colorScheme: 'red', label: 'ML negou' },
    indefinido: { colorScheme: 'gray', label: 'indefinido' },
};

const statsVazias = {
    hoje: { negociadas: 0, excluidos: 0, negados: 0, falhas: 0, custo_usd: 0 },
    semana: { negociadas: 0, excluidos: 0, negados: 0, falhas: 0, custo_usd: 0 },
    aguardando: 0,
};

const formatarData = (iso) => {
    if (!iso) return '-';
    const d = new Date(String(iso).replace(' ', 'T'));
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const dolar = (v) => `US$ ${Number(v || 0).toFixed(4)}`;

// YYYY-MM-DD no fuso do navegador (toISOString vira o dia às 21h em UTC-3).
const dataLocal = (d) => [
    d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0'),
].join('-');

const Estatistica = ({ label, valor, cor }) => (
    <Box borderRadius="md" border="1px solid" borderColor="gray.200" p={3}>
        <Text fontSize="xs" color="gray.500">{label}</Text>
        <Text fontSize="xl" fontWeight="bold" color={cor}>{valor}</Text>
    </Box>
);

const RetiraAtrasoTab = () => {
    const toast = useToast();
    const [carregando, setCarregando] = useState(true);
    const [config, setConfig] = useState(null);
    const [argumentos, setArgumentos] = useState([]);
    const [pendentes, setPendentes] = useState([]);
    const [execucoes, setExecucoes] = useState([]);
    const [negociacoes, setNegociacoes] = useState([]);
    const [stats, setStats] = useState(statsVazias);

    const [salvando, setSalvando] = useState(false);
    const [rodando, setRodando] = useState(false);
    const [decidindo, setDecidindo] = useState(null);
    const [textos, setTextos] = useState({});

    // Modal do argumento (novo/edição) e da conversa
    const argModal = useDisclosure();
    const [argEdit, setArgEdit] = useState(null);
    const [salvandoArg, setSalvandoArg] = useState(false);
    const conversaModal = useDisclosure();
    const [conversa, setConversa] = useState(null);
    const [carregandoConversa, setCarregandoConversa] = useState(false);

    // Exportação das conversas (CSV) por período
    const exportModal = useDisclosure();
    const [exportDe, setExportDe] = useState(() => dataLocal(new Date(Date.now() - 29 * 86400000)));
    const [exportAte, setExportAte] = useState(() => dataLocal(new Date()));
    const [exportando, setExportando] = useState(false);

    const carregar = useCallback(async (incluirConfig = false) => {
        try {
            const { data } = await getRetiraAtraso();
            if (incluirConfig) {
                setConfig({ ...data.config, horario_dias: normalizarGrade(data.config?.horario_dias) });
            } else {
                // Sem atropelar o que está sendo editado na tela: só os
                // campos que vêm do servidor e ninguém digita aqui.
                setConfig((c) => (c ? {
                    ...c,
                    agente_online: data.config?.agente_online,
                    agente_visto_em: data.config?.agente_visto_em,
                    ultima_execucao_em: data.config?.ultima_execucao_em,
                    dentro_horario: data.config?.dentro_horario,
                } : c));
            }
            setArgumentos(data.argumentos || []);
            setPendentes(data.pendentes || []);
            setExecucoes(data.execucoes || []);
            setNegociacoes(data.negociacoes || []);
            setStats(data.stats || statsVazias);
        } catch (e) {
            toast({ title: 'Erro ao carregar a automação de atrasos.', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setCarregando(false);
        }
    }, [toast]);

    useEffect(() => {
        carregar(true);
        const t = setInterval(() => carregar(false), 20000);
        return () => clearInterval(t);
    }, [carregar]);

    const salvarConfig = async (overrides = {}, mensagem = 'Configuração salva.') => {
        setSalvando(true);
        try {
            const { data } = await updateRetiraAtrasoConfig({ ...config, ...overrides });
            setConfig({ ...data, horario_dias: normalizarGrade(data?.horario_dias) });
            toast({ title: mensagem, status: 'success', duration: 3000, isClosable: true });
        } catch (e) {
            const msg = e?.response?.data?.message || 'Erro ao salvar a configuração.';
            toast({ title: msg, status: 'error', duration: 4000, isClosable: true });
        } finally {
            setSalvando(false);
        }
    };

    const rodarAgora = async () => {
        setRodando(true);
        try {
            await rodarRetiraAtrasoAgora();
            toast({
                title: 'Pedido enviado — o agente começa no próximo sinal (até 1 min).',
                status: 'info', duration: 4000, isClosable: true,
            });
            carregar(false);
        } catch (e) {
            const msg = e?.response?.data?.erro || 'Erro ao pedir a execução.';
            toast({ title: msg, status: 'error', duration: 4000, isClosable: true });
        } finally {
            setRodando(false);
        }
    };

    const decidir = async (negociacao, aprovar) => {
        setDecidindo(negociacao.id);
        try {
            if (aprovar) {
                const texto = textos[negociacao.id];
                await aprovarRetiraAtrasoNegociacao(negociacao.id, texto ? { abertura: texto } : {});
                toast({ title: 'Argumento aprovado — o agente já pode enviar.', status: 'success', duration: 3000, isClosable: true });
            } else {
                await negarRetiraAtrasoNegociacao(negociacao.id, {});
                toast({ title: 'Argumento negado. Essa venda não será negociada.', status: 'info', duration: 3000, isClosable: true });
            }
            setTextos((t) => {
                const novo = { ...t };
                delete novo[negociacao.id];
                return novo;
            });
            carregar(false);
        } catch (e) {
            const msg = e?.response?.data?.erro || 'Erro ao registrar a decisão.';
            toast({ title: msg, status: 'error', duration: 4000, isClosable: true });
        } finally {
            setDecidindo(null);
        }
    };

    const abrirArgumento = (argumento) => {
        setArgEdit(argumento || { nome: '', texto: '', quando_usar: '', ativo: true });
        argModal.onOpen();
    };

    const salvarArgumento = async () => {
        if (!argEdit?.nome?.trim() || !argEdit?.texto?.trim()) {
            toast({ title: 'Nome e texto do argumento são obrigatórios.', status: 'warning', duration: 3000, isClosable: true });
            return;
        }
        setSalvandoArg(true);
        try {
            const payload = {
                nome: argEdit.nome.trim(),
                texto: argEdit.texto.trim(),
                quando_usar: argEdit.quando_usar?.trim() || null,
                ativo: !!argEdit.ativo,
            };
            if (argEdit.id) await atualizarRetiraAtrasoArgumento(argEdit.id, payload);
            else await criarRetiraAtrasoArgumento(payload);
            toast({ title: 'Argumento salvo.', status: 'success', duration: 3000, isClosable: true });
            argModal.onClose();
            setArgEdit(null);
            carregar(false);
        } catch (e) {
            toast({ title: 'Erro ao salvar o argumento.', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setSalvandoArg(false);
        }
    };

    const alternarArgumento = async (argumento) => {
        try {
            await atualizarRetiraAtrasoArgumento(argumento.id, { ativo: !argumento.ativo });
            carregar(false);
        } catch (e) {
            toast({ title: 'Erro ao atualizar o argumento.', status: 'error', duration: 4000, isClosable: true });
        }
    };

    const removerArgumento = async (argumento) => {
        if (!window.confirm(`Remover o argumento "${argumento.nome}"?`)) return;
        try {
            await removerRetiraAtrasoArgumento(argumento.id);
            toast({ title: 'Argumento removido.', status: 'info', duration: 3000, isClosable: true });
            carregar(false);
        } catch (e) {
            toast({ title: 'Erro ao remover o argumento.', status: 'error', duration: 4000, isClosable: true });
        }
    };

    const verConversa = async (negociacao) => {
        setConversa(null);
        setCarregandoConversa(true);
        conversaModal.onOpen();
        try {
            const { data } = await getRetiraAtrasoNegociacao(negociacao.id);
            setConversa(data);
        } catch (e) {
            toast({ title: 'Erro ao abrir a conversa.', status: 'error', duration: 4000, isClosable: true });
            conversaModal.onClose();
        } finally {
            setCarregandoConversa(false);
        }
    };

    const exportarConversas = async () => {
        setExportando(true);
        try {
            const { data } = await exportarRetiraAtrasoConversas({ de: exportDe, ate: exportAte });
            const url = URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `retira-atraso-conversas-${exportDe}_${exportAte}.csv`;
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            URL.revokeObjectURL(url);
            exportModal.onClose();
        } catch (e) {
            const vazio = e?.response?.status === 404;
            toast({
                title: vazio ? 'Nenhuma conversa nesse período.' : 'Erro ao exportar as conversas.',
                status: vazio ? 'info' : 'error', duration: 4000, isClosable: true,
            });
        } finally {
            setExportando(false);
        }
    };

    if (carregando || !config) {
        return (
            <Flex justify="center" py={10}><Spinner size="lg" color="orange.400" /></Flex>
        );
    }

    const setCampo = (campo, valor) => setConfig((c) => ({ ...c, [campo]: valor }));

    const setDia = (dia, patch) => setConfig((c) => {
        const grade = normalizarGrade(c.horario_dias);
        grade[dia] = { ...grade[dia], ...patch };
        return { ...c, horario_dias: grade };
    });

    return (
        <VStack spacing={5} align="stretch">

            {/* Status */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Flex align="center" justify="space-between" wrap="wrap" gap={4}>
                    <Box flex="1" minW={{ base: 'full', md: '260px' }} maxW="820px">
                        <Text fontWeight="bold" fontSize="lg">Retirar atrasos do Mercado Livre</Text>
                        <Text fontSize="sm" color="gray.500">
                            O agente lê as vendas em atraso em Métricas &gt; Meu atendimento &gt; Envios
                            incorretos, monta um argumento com a IA e negocia a exclusão com o suporte do ML —
                            uma conversa nova por venda. Ele roda na máquina onde as contas do Mercado Livre e o
                            WhatsApp estão logados; esta tela manda a configuração, aprova os argumentos e guarda
                            a resposta do ML.
                        </Text>
                    </Box>
                    <HStack spacing={3}>
                        <Badge
                            colorScheme={config.ativo ? 'green' : 'gray'}
                            fontSize="0.8em" px={2} py={1} borderRadius="md"
                        >
                            {config.ativo ? 'LIGADO' : 'DESLIGADO'}
                        </Badge>
                        <Switch
                            size="lg" colorScheme="orange"
                            isChecked={!!config.ativo}
                            isDisabled={salvando}
                            onChange={(e) => salvarConfig(
                                { ativo: e.target.checked },
                                e.target.checked ? 'Automação ligada.' : 'Automação desligada.'
                            )}
                        />
                    </HStack>
                </Flex>

                <Divider my={4} />

                <Flex gap={3} wrap="wrap" align="center">
                    <Badge
                        flexShrink={0}
                        colorScheme={config.agente_online ? 'green' : 'red'}
                        variant="subtle" px={2} py={1} borderRadius="md"
                    >
                        {config.agente_online ? 'Agente conectado' : 'Agente offline'}
                    </Badge>
                    {config.horario_restrito && !config.dentro_horario && (
                        <Badge flexShrink={0} colorScheme="orange" variant="subtle" px={2} py={1} borderRadius="md">
                            Fora do horário comercial
                        </Badge>
                    )}
                    <Text fontSize="xs" color="gray.500">
                        {config.agente_info ? `${config.agente_info} — ` : ''}
                        último sinal: {formatarData(config.agente_visto_em)} | última execução: {formatarData(config.ultima_execucao_em)}
                    </Text>
                    <Box flex="1" display={{ base: 'none', md: 'block' }} />
                    <Button
                        size="sm" leftIcon={<FaPlay />} colorScheme="orange"
                        onClick={rodarAgora} isLoading={rodando} isDisabled={!config.ativo}
                        flexShrink={0}
                    >
                        Rodar agora
                    </Button>
                    <IconButton aria-label="Atualizar" icon={<FaSyncAlt />} size="sm" variant="ghost" onClick={() => carregar(false)} />
                </Flex>

                {!config.agente_online && config.ativo && (
                    <Alert status="warning" mt={4} borderRadius="md" fontSize="sm">
                        <AlertIcon />
                        O agente não está dando sinal. Na máquina com os logins do ML e do WhatsApp, rode
                        <b>&nbsp;npm run agent&nbsp;</b> dentro da pasta <b>automatiza_retira_atraso</b>.
                    </Alert>
                )}

                <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={3} mt={4}>
                    <Estatistica label="Aguardando aprovação" valor={stats.aguardando} cor="purple.600" />
                    <Estatistica label="Negociadas hoje" valor={stats.hoje.negociadas} />
                    <Estatistica label="Atrasos excluídos hoje" valor={stats.hoje.excluidos} cor="green.600" />
                    <Estatistica label="ML negou hoje" valor={stats.hoje.negados} cor="red.500" />
                    <Estatistica label="Excluídos (7 dias)" valor={stats.semana.excluidos} cor="green.600" />
                    <Estatistica label="Custo IA (7 dias)" valor={dolar(stats.semana.custo_usd)} />
                </SimpleGrid>
            </Box>

            {/* Fila de aprovação */}
            {pendentes.length > 0 && (
                <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                    <Text fontWeight="bold" fontSize="lg" mb={1}>
                        Argumentos esperando aprovação ({pendentes.length})
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={4}>
                        O agente está parado esperando a sua decisão (até {config.aprovacao_timeout_min} min por
                        atendimento). Dá pra editar o texto antes de aprovar. Negar descarta o atendimento
                        inteiro — as vendas dele não voltam para a fila nas próximas execuções.
                    </Text>
                    <VStack spacing={4} align="stretch">
                        {pendentes.map((n) => {
                            const vendas = n.vendas?.length ? n.vendas : [n];
                            return (
                            <Box key={n.id} borderRadius="md" border="1px solid" borderColor="purple.200" p={4}>
                                <Flex justify="space-between" wrap="wrap" gap={2} mb={2}>
                                    <Box>
                                        <Text fontWeight="medium" fontSize="sm">
                                            {vendas.length > 1
                                                ? `${vendas.length} vendas neste atendimento`
                                                : `Venda #${n.order_id}`} — conta {n.conta}
                                        </Text>
                                        {vendas.map((v) => (
                                            <Text key={v.order_id} fontSize="xs" color="gray.500">
                                                #{v.order_id} — {v.titulo || 'produto não identificado'} | {v.motivo || 'motivo n/d'} | {v.servico || 'envio n/d'}
                                            </Text>
                                        ))}
                                    </Box>
                                    <HStack>
                                        {n.tentativa > 1 && <Badge colorScheme="orange">tentativa {n.tentativa}</Badge>}
                                        {n.estrategia && <Badge colorScheme="purple">{n.estrategia}</Badge>}
                                    </HStack>
                                </Flex>
                                <Textarea
                                    size="sm" rows={4}
                                    value={textos[n.id] ?? n.abertura ?? ''}
                                    onChange={(e) => setTextos((t) => ({ ...t, [n.id]: e.target.value }))}
                                />
                                <Stack
                                    direction={{ base: 'column', sm: 'row' }} mt={3} spacing={3}
                                    align={{ base: 'stretch', sm: 'center' }}
                                >
                                    <Button
                                        size="sm" colorScheme="green" leftIcon={<FaCheck />}
                                        isLoading={decidindo === n.id}
                                        onClick={() => decidir(n, true)}
                                    >
                                        Aprovar e enviar
                                    </Button>
                                    <Button
                                        size="sm" colorScheme="red" variant="outline" leftIcon={<FaTimes />}
                                        isLoading={decidindo === n.id}
                                        onClick={() => decidir(n, false)}
                                    >
                                        Negar
                                    </Button>
                                    <Text fontSize="xs" color="gray.500">
                                        gerado em {formatarData(n.created_at)}
                                    </Text>
                                </Stack>
                            </Box>
                            );
                        })}
                    </VStack>
                </Box>
            )}

            {/* Configurações */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Text fontWeight="bold" fontSize="lg" mb={4}>Configurações</Text>

                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                    <FormControl>
                        <FormLabel fontSize="sm">Rodar de quantos em quantos minutos</FormLabel>
                        <NumberInput
                            size="sm" min={5} max={10080}
                            value={config.intervalo_minutos}
                            onChange={(_, v) => setCampo('intervalo_minutos', Number.isNaN(v) ? 5 : v)}
                        >
                            <NumberInputField />
                        </NumberInput>
                        <FormHelperText fontSize="xs">720 = a cada 12 horas.</FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Vendas por atendimento</FormLabel>
                        <NumberInput
                            size="sm" min={0} max={500}
                            value={config.max_vendas}
                            onChange={(_, v) => setCampo('max_vendas', Number.isNaN(v) ? 0 : v)}
                        >
                            <NumberInputField />
                        </NumberInput>
                        <FormHelperText fontSize="xs">
                            Todas entram na MESMA conversa com o suporte. 0 = pega todas as que estiverem em atraso.
                        </FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Tentativas por venda</FormLabel>
                        <NumberInput
                            size="sm" min={1} max={10}
                            value={config.max_tentativas}
                            onChange={(_, v) => setCampo('max_tentativas', Number.isNaN(v) ? 1 : v)}
                        >
                            <NumberInputField />
                        </NumberInput>
                        <FormHelperText fontSize="xs">
                            Quantas conversas o bot pode abrir para a mesma venda. Se a tentativa falhar
                            (chat não abriu, suporte não respondeu), ele repete ali mesmo até esse limite.
                        </FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Insistir quando o ML negar</FormLabel>
                        <Flex align="center" h="32px">
                            <Switch
                                colorScheme="orange" mr={2}
                                isChecked={!!config.retentar_negados}
                                onChange={(e) => setCampo('retentar_negados', e.target.checked)}
                            />
                            <Text fontSize="sm">{config.retentar_negados ? 'Tenta de novo' : 'Não insiste'}</Text>
                        </Flex>
                        <FormHelperText fontSize="xs">
                            Ligado, a venda em que o suporte negou volta para a fila numa próxima execução,
                            dentro do limite de tentativas. Conversa que acabou sem desfecho volta sozinha, mesmo
                            com isso desligado. Venda que você negou aqui na tela nunca volta.
                        </FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Retirar apenas vendas já enviadas</FormLabel>
                        <Flex align="center" h="32px">
                            <Switch
                                colorScheme="orange" mr={2}
                                isChecked={!!config.somente_enviadas}
                                onChange={(e) => setCampo('somente_enviadas', e.target.checked)}
                            />
                            <Text fontSize="sm">{config.somente_enviadas ? 'Só as já enviadas' : 'Todas em atraso'}</Text>
                        </Flex>
                        <FormHelperText fontSize="xs">
                            Ligado, só entra na fila a venda cujo pacote já saiu daqui (despachado, a caminho
                            ou entregue). Pedir a revisão de um atraso com o pedido ainda parado em casa
                            entrega ao atendente a prova contra a gente — ele vê que a etiqueta nem foi emitida.
                        </FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Contas do Mercado Livre</FormLabel>
                        <CheckboxGroup
                            colorScheme="orange"
                            value={config.contas || []}
                            onChange={(v) => setCampo('contas', v.length ? v : ['1'])}
                        >
                            <Stack direction="row" spacing={4} pt={1}>
                                {CONTAS_ML.map((c) => (
                                    <Checkbox key={c} value={c}>Conta {c}</Checkbox>
                                ))}
                            </Stack>
                        </CheckboxGroup>
                        <FormHelperText fontSize="xs">Cada conta roda em sequência, com o perfil dela.</FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Canal do atendimento</FormLabel>
                        <Select size="sm" value={config.canal} onChange={(e) => setCampo('canal', e.target.value)}>
                            {CANAIS.map((c) => <option key={c.valor} value={c.valor}>{c.label}</option>)}
                        </Select>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Aprovação dos argumentos</FormLabel>
                        <Select
                            size="sm" value={config.modo_aprovacao}
                            onChange={(e) => setCampo('modo_aprovacao', e.target.value)}
                        >
                            <option value="automatico">Automática (envia sem passar por aqui)</option>
                            <option value="manual">Manual (cada argumento precisa ser aprovado)</option>
                        </Select>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Tempo de espera da aprovação (min)</FormLabel>
                        <NumberInput
                            size="sm" min={1} max={1440}
                            value={config.aprovacao_timeout_min}
                            onChange={(_, v) => setCampo('aprovacao_timeout_min', Number.isNaN(v) ? 1 : v)}
                        >
                            <NumberInputField />
                        </NumberInput>
                        <FormHelperText fontSize="xs">Passou disso sem decisão, o agente pula a venda.</FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Máximo de respostas por atendimento</FormLabel>
                        <NumberInput
                            size="sm" min={1} max={60}
                            value={config.max_turnos}
                            onChange={(_, v) => setCampo('max_turnos', Number.isNaN(v) ? 1 : v)}
                        >
                            <NumberInputField />
                        </NumberInput>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Pausa entre tentativas (segundos)</FormLabel>
                        <NumberInput
                            size="sm" min={0} max={3600}
                            value={config.delay_entre_vendas_seg}
                            onChange={(_, v) => setCampo('delay_entre_vendas_seg', Number.isNaN(v) ? 0 : v)}
                        >
                            <NumberInputField />
                        </NumberInput>
                        <FormHelperText fontSize="xs">
                            Quanto o agente espera antes de refazer o atendimento que não deu certo.
                        </FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Modelo da IA</FormLabel>
                        <Input size="sm" value={config.modelo || ''} onChange={(e) => setCampo('modelo', e.target.value)} />
                    </FormControl>
                </SimpleGrid>

                <Divider my={4} />

                <Flex align="center" justify="space-between" wrap="wrap" gap={3}>
                    <Box maxW="720px">
                        <Text fontWeight="bold" fontSize="md">Só rodar em horário comercial</Text>
                        <Text fontSize="sm" color="gray.500">
                            Fora da janela o agente não abre atendimento novo. De madrugada e no fim de
                            semana o suporte do ML atende devagar ou não atende, e a venda queima
                            tentativa à toa. O atendimento que já estava em andamento segue até o fim, e o
                            botão "Rodar agora" passa por cima da janela.
                        </Text>
                    </Box>
                    <Switch
                        size="lg" colorScheme="orange"
                        isChecked={!!config.horario_restrito}
                        isDisabled={salvando}
                        onChange={(e) => salvarConfig(
                            { horario_restrito: e.target.checked },
                            e.target.checked ? 'Agora só roda em horário comercial.' : 'Horário liberado: roda a qualquer hora.'
                        )}
                    />
                </Flex>

                {config.horario_restrito && (
                    <VStack align="stretch" spacing={2} mt={3}>
                        {DIAS_SEMANA.map((d) => {
                            const dia = (config.horario_dias || [])[d.v] || { ativo: false, inicio: '08:00', fim: '18:00' };
                            return (
                                <Flex
                                    key={d.v} align="center" gap={3} wrap="wrap"
                                    py={2} px={3} borderRadius="md"
                                    bg={dia.ativo ? 'white' : 'gray.50'}
                                    border="1px solid" borderColor="gray.200"
                                >
                                    <Text w="42px" fontWeight="medium" fontSize="sm">{d.label}</Text>
                                    <Switch
                                        size="sm" colorScheme="orange"
                                        isChecked={dia.ativo}
                                        isDisabled={salvando}
                                        onChange={(e) => setDia(d.v, { ativo: e.target.checked })}
                                    />
                                    {dia.ativo ? (
                                        <>
                                            <Input
                                                type="time" size="sm" maxW="120px" bg="white"
                                                value={dia.inicio || '08:00'}
                                                onChange={(e) => setDia(d.v, { inicio: e.target.value })}
                                            />
                                            <Text fontSize="sm">às</Text>
                                            <Input
                                                type="time" size="sm" maxW="120px" bg="white"
                                                value={dia.fim || '18:00'}
                                                onChange={(e) => setDia(d.v, { fim: e.target.value })}
                                            />
                                        </>
                                    ) : (
                                        <Text fontSize="sm" color="gray.400">não roda</Text>
                                    )}
                                </Flex>
                            );
                        })}
                        <Text fontSize="xs" color="gray.500">
                            Horário de Brasília. As mudanças nos dias e horas valem depois de "Salvar
                            configurações", ali embaixo.
                        </Text>
                    </VStack>
                )}

                <Divider my={4} />

                <Flex gap={6} wrap="wrap" align="center">
                    <FormControl display="flex" alignItems="center" w="auto">
                        <Switch
                            id="dry-run" colorScheme="orange" mr={2}
                            isChecked={!!config.dry_run}
                            onChange={(e) => setCampo('dry_run', e.target.checked)}
                        />
                        <FormLabel htmlFor="dry-run" mb={0} fontSize="sm">
                            <Tooltip label="Gera o argumento e registra aqui, mas não manda nada para o suporte do ML.">
                                Modo teste (não envia)
                            </Tooltip>
                        </FormLabel>
                    </FormControl>

                    <FormControl display="flex" alignItems="center" w="auto">
                        <Switch
                            id="headless" colorScheme="orange" mr={2}
                            isChecked={!!config.headless}
                            onChange={(e) => setCampo('headless', e.target.checked)}
                        />
                        <FormLabel htmlFor="headless" mb={0} fontSize="sm">
                            <Tooltip label="Sem janela do navegador. O WhatsApp Web costuma reclamar nesse modo.">
                                Navegador escondido
                            </Tooltip>
                        </FormLabel>
                    </FormControl>

                    <Box flex="1" display={{ base: 'none', md: 'block' }} />
                    <Button
                        size="sm" colorScheme="orange" onClick={() => salvarConfig()} isLoading={salvando}
                        w={{ base: 'full', md: 'auto' }}
                    >
                        Salvar configurações
                    </Button>
                </Flex>
            </Box>

            {/* Argumentos */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Flex align="center" justify="space-between" mb={1} wrap="wrap" gap={3}>
                    <Text fontWeight="bold" fontSize="lg">Argumentos do bot</Text>
                    <Button size="sm" leftIcon={<FaPlus />} colorScheme="orange" variant="outline" onClick={() => abrirArgumento(null)}>
                        Novo argumento
                    </Button>
                </Flex>
                <Text fontSize="sm" color="gray.500" mb={4}>
                    A IA escolhe um destes por venda, olhando o produto, o serviço de envio e o motivo do atraso.
                    Desligar todos faz o agente voltar para a lista embutida no código dele.
                </Text>

                <TableContainer>
                    <Table size="sm" variant="simple">
                        <Thead>
                            <Tr>
                                <Th>Nome</Th><Th>Quando usar</Th><Th>Texto</Th><Th>Ativo</Th><Th></Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {argumentos.map((a) => (
                                <Tr key={a.id}>
                                    <Td fontSize="xs" fontWeight="medium">{a.nome}</Td>
                                    <Td fontSize="xs" color="gray.500">{a.quando_usar || '-'}</Td>
                                    <Td fontSize="xs" whiteSpace="normal" maxW={{ base: '220px', md: '420px' }}>
                                        {a.texto.length > 160 ? `${a.texto.slice(0, 160)}...` : a.texto}
                                    </Td>
                                    <Td>
                                        <Switch
                                            size="sm" colorScheme="orange"
                                            isChecked={!!a.ativo}
                                            onChange={() => alternarArgumento(a)}
                                        />
                                    </Td>
                                    <Td>
                                        <HStack spacing={1}>
                                            <IconButton aria-label="Editar" icon={<FaEdit />} size="xs" variant="ghost" onClick={() => abrirArgumento(a)} />
                                            <IconButton aria-label="Remover" icon={<FaTrash />} size="xs" variant="ghost" colorScheme="red" onClick={() => removerArgumento(a)} />
                                        </HStack>
                                    </Td>
                                </Tr>
                            ))}
                            {argumentos.length === 0 && (
                                <Tr><Td colSpan={5} fontSize="xs" color="gray.500">Nenhum argumento cadastrado.</Td></Tr>
                            )}
                        </Tbody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Negociações */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Flex align="center" justify="space-between" wrap="wrap" gap={3} mb={4}>
                    <Text fontWeight="bold" fontSize="lg">Últimas negociações</Text>
                    <Button
                        size="sm" leftIcon={<FaDownload />} colorScheme="orange" variant="outline"
                        onClick={exportModal.onOpen}
                    >
                        Exportar conversas
                    </Button>
                </Flex>
                <TableContainer>
                    <Table size="sm" variant="simple">
                        <Thead>
                            <Tr>
                                <Th>Quando</Th><Th>Conta</Th><Th>Venda</Th><Th isNumeric>Tent.</Th><Th>Argumento</Th>
                                <Th>Status</Th><Th>Resposta do ML</Th><Th isNumeric>Turnos</Th>
                                <Th isNumeric>Custo</Th><Th></Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {negociacoes.map((n) => {
                                const st = STATUS_BADGE[n.status] || { colorScheme: 'gray', label: n.status };
                                const rs = RESULTADO_BADGE[n.resultado];
                                // Quantas vendas foram tratadas na mesma conversa.
                                const noLote = n.lote
                                    ? negociacoes.filter((o) => o.lote === n.lote).length
                                    : 1;
                                return (
                                    <Tr key={n.id}>
                                        <Td fontSize="xs">{formatarData(n.created_at)}</Td>
                                        <Td fontSize="xs">{n.conta}</Td>
                                        <Td fontSize="xs">
                                            <Text>#{n.order_id}</Text>
                                            <Text color="gray.500" noOfLines={1} maxW="220px">{n.titulo}</Text>
                                            {noLote > 1 && (
                                                <Badge colorScheme="blue" fontSize="0.7em">
                                                    atendimento com {noLote} vendas
                                                </Badge>
                                            )}
                                        </Td>
                                        <Td fontSize="xs" isNumeric>{n.tentativa || 1}</Td>
                                        <Td fontSize="xs">{n.estrategia || '-'}</Td>
                                        <Td>
                                            <Badge colorScheme={st.colorScheme} fontSize="0.7em">{st.label}</Badge>
                                            {n.dry_run && <Badge ml={1} colorScheme="gray" fontSize="0.7em">teste</Badge>}
                                            {n.erro && (
                                                <Tooltip label={n.erro}>
                                                    <Text fontSize="xs" color="red.500" noOfLines={1} maxW="180px">{n.erro}</Text>
                                                </Tooltip>
                                            )}
                                        </Td>
                                        <Td>{rs ? <Badge colorScheme={rs.colorScheme} fontSize="0.7em">{rs.label}</Badge> : '-'}</Td>
                                        <Td fontSize="xs" isNumeric>{n.turnos}</Td>
                                        <Td fontSize="xs" isNumeric>{dolar(n.custo_usd)}</Td>
                                        <Td>
                                            <IconButton
                                                aria-label="Ver conversa" icon={<FaEye />} size="xs" variant="ghost"
                                                onClick={() => verConversa(n)}
                                            />
                                        </Td>
                                    </Tr>
                                );
                            })}
                            {negociacoes.length === 0 && (
                                <Tr><Td colSpan={10} fontSize="xs" color="gray.500">Nenhuma negociação ainda.</Td></Tr>
                            )}
                        </Tbody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Execuções */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Text fontWeight="bold" fontSize="lg" mb={4}>Execuções</Text>
                <TableContainer>
                    <Table size="sm" variant="simple">
                        <Thead>
                            <Tr>
                                <Th>Início</Th><Th>Fim</Th><Th>Conta</Th><Th>Origem</Th><Th>Status</Th>
                                <Th isNumeric>Atrasos</Th><Th isNumeric>Pendentes</Th>
                                <Th isNumeric>Concluídas</Th><Th isNumeric>Falhas</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {execucoes.map((e) => (
                                <Tr key={e.id}>
                                    <Td fontSize="xs">{formatarData(e.iniciada_em)}</Td>
                                    <Td fontSize="xs">{formatarData(e.finalizada_em)}</Td>
                                    <Td fontSize="xs">{e.conta}</Td>
                                    <Td fontSize="xs">{e.origem}</Td>
                                    <Td>
                                        <Badge
                                            colorScheme={e.status === 'concluida' ? 'green' : e.status === 'erro' ? 'red' : 'blue'}
                                            fontSize="0.7em"
                                        >
                                            {e.status}
                                        </Badge>
                                        {e.erro && (
                                            <Tooltip label={e.erro}>
                                                <Text fontSize="xs" color="red.500" noOfLines={1} maxW="220px">{e.erro}</Text>
                                            </Tooltip>
                                        )}
                                    </Td>
                                    <Td fontSize="xs" isNumeric>{e.total_atrasos}</Td>
                                    <Td fontSize="xs" isNumeric>{e.pendentes}</Td>
                                    <Td fontSize="xs" isNumeric>{e.concluidas}</Td>
                                    <Td fontSize="xs" isNumeric>{e.falhas}</Td>
                                </Tr>
                            ))}
                            {execucoes.length === 0 && (
                                <Tr><Td colSpan={9} fontSize="xs" color="gray.500">Nenhuma execução ainda.</Td></Tr>
                            )}
                        </Tbody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Modal do argumento */}
            <Modal isOpen={argModal.isOpen} onClose={argModal.onClose} size={{ base: 'full', md: 'xl' }}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{argEdit?.id ? 'Editar argumento' : 'Novo argumento'}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <FormControl>
                                <FormLabel fontSize="sm">Nome</FormLabel>
                                <Input
                                    size="sm" maxLength={80}
                                    value={argEdit?.nome || ''}
                                    onChange={(e) => setArgEdit((a) => ({ ...a, nome: e.target.value }))}
                                />
                                <FormHelperText fontSize="xs">É por esse nome que a IA declara a estratégia escolhida.</FormHelperText>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">Quando usar</FormLabel>
                                <Input
                                    size="sm" maxLength={255}
                                    value={argEdit?.quando_usar || ''}
                                    onChange={(e) => setArgEdit((a) => ({ ...a, quando_usar: e.target.value }))}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">Texto do argumento</FormLabel>
                                <Textarea
                                    size="sm" rows={7}
                                    value={argEdit?.texto || ''}
                                    onChange={(e) => setArgEdit((a) => ({ ...a, texto: e.target.value }))}
                                />
                            </FormControl>
                            <FormControl display="flex" alignItems="center">
                                <Switch
                                    colorScheme="orange" mr={2}
                                    isChecked={!!argEdit?.ativo}
                                    onChange={(e) => setArgEdit((a) => ({ ...a, ativo: e.target.checked }))}
                                />
                                <FormLabel mb={0} fontSize="sm">Ativo</FormLabel>
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button size="sm" variant="ghost" mr={3} onClick={argModal.onClose}>Cancelar</Button>
                        <Button size="sm" colorScheme="orange" onClick={salvarArgumento} isLoading={salvandoArg}>
                            Salvar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Modal da conversa */}
            <Modal
                isOpen={conversaModal.isOpen} onClose={conversaModal.onClose}
                size={{ base: 'full', md: '2xl' }} scrollBehavior="inside"
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        {conversa ? `Venda #${conversa.order_id} — conta ${conversa.conta}` : 'Conversa'}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {carregandoConversa && <Flex justify="center" py={6}><Spinner color="orange.400" /></Flex>}
                        {conversa && (
                            <VStack align="stretch" spacing={3}>
                                <Box fontSize="xs" color="gray.500">
                                    {conversa.titulo} | {conversa.motivo || 'motivo n/d'} | {conversa.servico || 'envio n/d'} | canal: {conversa.canal}
                                    {conversa.estrategia && ` | argumento: ${conversa.estrategia}`}
                                    {conversa.aprovador && ` | aprovado por ${conversa.aprovador.name}`}
                                </Box>
                                {conversa.erro && (
                                    <Alert status="warning" borderRadius="md" fontSize="sm">
                                        <AlertIcon />{conversa.erro}
                                    </Alert>
                                )}
                                {(conversa.transcript || []).map((linha, i) => (
                                    <Box
                                        key={i}
                                        alignSelf={linha.de === 'bot' ? 'flex-end' : 'flex-start'}
                                        bg={linha.de === 'bot' ? 'orange.50' : 'gray.100'}
                                        borderRadius="md" px={3} py={2} maxW="85%"
                                    >
                                        <Text fontSize="10px" color="gray.500" mb={1}>
                                            {linha.de === 'bot' ? 'Nós (IA)' : 'Suporte do Mercado Livre'}
                                        </Text>
                                        <Text fontSize="sm" whiteSpace="pre-wrap">{linha.texto}</Text>
                                    </Box>
                                ))}
                                {(!conversa.transcript || conversa.transcript.length === 0) && (
                                    <Text fontSize="sm" color="gray.500">
                                        Sem conversa gravada — o argumento não chegou a ser enviado.
                                    </Text>
                                )}
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button size="sm" onClick={conversaModal.onClose}>Fechar</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Modal da exportação */}
            <Modal isOpen={exportModal.isOpen} onClose={exportModal.onClose} size={{ base: 'full', md: 'md' }}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Exportar conversas</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack align="stretch" spacing={4}>
                            <Text fontSize="sm" color="gray.500">
                                Planilha CSV (abre no Excel) com as conversas com o suporte do ML, uma linha por
                                mensagem. Cada atendimento aparece uma vez, com as vendas tratadas nele.
                            </Text>
                            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                                <FormControl>
                                    <FormLabel fontSize="sm">De</FormLabel>
                                    <Input
                                        type="date" size="sm" value={exportDe} max={exportAte}
                                        onChange={(e) => setExportDe(e.target.value)}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="sm">Até</FormLabel>
                                    <Input
                                        type="date" size="sm" value={exportAte} min={exportDe}
                                        onChange={(e) => setExportAte(e.target.value)}
                                    />
                                </FormControl>
                            </SimpleGrid>
                            {exportDe && exportAte && exportDe > exportAte && (
                                <Text fontSize="xs" color="red.500">A data inicial é depois da final.</Text>
                            )}
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button size="sm" variant="ghost" mr={3} onClick={exportModal.onClose}>Cancelar</Button>
                        <Button
                            size="sm" colorScheme="orange" leftIcon={<FaDownload />}
                            onClick={exportarConversas} isLoading={exportando}
                            isDisabled={!exportDe || !exportAte || exportDe > exportAte}
                        >
                            Exportar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </VStack>
    );
};

export default RetiraAtrasoTab;
